import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { AppStorePoliciesDocument } from 'generated/resources/graphql'

import type { Tab } from 'modules/core/components/Card/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'
import { Navigation } from 'modules/core/lib/navigation/context'

import AppDescription from 'modules/app_store/components/AppDescription'
import Cols from 'modules/app_store/components/Cols'
import ImagesGallery from 'modules/app_store/components/ImagesGallery'
import Sidebar from 'modules/app_store/components/Sidebar'
import useFetchData from 'modules/app_store/hooks/useFetchData'
import configurator from 'modules/app_store/lib/SubscriptionAddonConfigurator'
import { isAtsSuscriptionAddon } from 'modules/ats/lib/utils/integrations'
import type SubscriptionAddon from 'modules/billing/store/models/SubscriptionAddon'
import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'
import { getFirstStepVideo } from 'modules/onboarding/lib/firstSteps/videos'

import buildTabs from './buildTabs'

interface ContentProps {
  subscriptionAddon: SubscriptionAddon | null | undefined
}

const Content = ({ subscriptionAddon }: ContentProps) => {
  const { navigation } = useNavigation()

  const {
    access: { locale },
  } = useCurrentSession()

  if (!subscriptionAddon) return null

  const config = configurator.getSubscriptionAddonConfig(subscriptionAddon, navigation)

  const thumbs = config.thumbs ? config.thumbs : config.images
  const video = getFirstStepVideo('appsSection', config.id, locale)

  return (
    <Cols>
      <Sidebar subscriptionAddon={subscriptionAddon} />
      <div>
        <ImagesGallery images={config.images} thumbs={thumbs} video={video} />
        <AppDescription subscriptionAddon={subscriptionAddon} />
      </div>
    </Cols>
  )
}

const backTo = (
  subscriptionAddon: SubscriptionAddon | null | undefined,
  navigation: Navigation<NavigationPaths>
): string => {
  if (isAtsSuscriptionAddon(subscriptionAddon?.id)) {
    return navigation.settings.ats.integrations.fullPath()
  }

  if (subscriptionAddon?.type === 'integration') {
    return navigation.discover.marketplace.fullPath()
  }

  return navigation.appStore.fullPath()
}

const GeneralPage = () => {
  const { id } = useParams()
  const [tabs, setTabs] = useState<Tab[]>([])
  const { loading, isNotFound, subscriptionAddon } = useFetchData(id)
  const { navigation } = useNavigation()
  const location = useLocation()
  const { data: policiesData } = useQuery(AppStorePoliciesDocument)

  const canSeeAppStore = policiesData?.integrationsBase._policies.seeAppStore.can ?? false

  useEffect(() => {
    async function fetchData() {
      const value = subscriptionAddon
        ? await buildTabs(subscriptionAddon, navigation, canSeeAppStore)
        : []
      setTabs(value)
    }

    fetchData()

    track(trackingEvents.appStore.goToAnyApp, {
      app_name: subscriptionAddon?.id,
      app_page_path: location.pathname,
      app_page_url: window.location.href,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionAddon, canSeeAppStore])

  const disableGeneralPage = useMemo(() => {
    switch (id) {
      case 'payroll_bundle':
        return hasFeature('payroll')
      default:
        return false
    }
  }, [id])

  return (
    <Page
      isLoading={loading}
      notFound={{
        active: isNotFound || disableGeneralPage,
        title: i18n.t('errors.page_not_found.title'),
        description: i18n.t('errors.page_not_found.description'),
      }}
    >
      <Page.Header>
        <Header.Tabs backTo={backTo(subscriptionAddon, navigation)} tabs={tabs} />
      </Page.Header>
      <Page.Body>
        <Content subscriptionAddon={subscriptionAddon} />
      </Page.Body>
    </Page>
  )
}

export default GeneralPage
